import { useRef } from "react";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { getFileUrl } from "@/lib/api";
import { CHECKLIST_CATEGORIES } from "@/components/MechanicalChecklist";

const RATING_LABELS = {
  0: { label: "0", description: "Non verificat", bg: "#52525b" },
  1: { label: "1", description: "Defect", bg: "#dc2626" },
  2: { label: "2", description: "De reparat", bg: "#f97316" },
  3: { label: "3", description: "Funcțional", bg: "#eab308" },
  4: { label: "4", description: "Bun", bg: "#84cc16" },
  5: { label: "5", description: "Excelent", bg: "#22c55e" },
};

export const PrintScooterSheet = ({ scooter }) => {
  const handlePrint = () => {
    // Get QR code SVG from the hidden element
    const qrElement = document.getElementById(`print-qr-${scooter.scooter_id}`);
    const qrSvg = qrElement ? qrElement.innerHTML : '';
    
    const printContent = generatePrintHTML(scooter, qrSvg);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 500);
    };
  };

  const qrUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;

  return (
    <>
      {/* Hidden QR code for print */}
      <div id={`print-qr-${scooter.scooter_id}`} style={{ position: 'absolute', left: '-9999px' }}>
        <QRCode value={qrUrl} size={200} level="H" />
      </div>
      
      <Button
        variant="outline"
        onClick={handlePrint}
        data-testid="btn-print-sheet"
        className="bg-primary/10 border-primary/30 hover:bg-primary/20"
      >
        <Printer className="w-4 h-4 mr-2" />
        Printează Fișa
      </Button>
    </>
  );
};

function generatePrintHTML(scooter, qrSvg) {
  const qrUrl = `${window.location.origin}/scooter/${scooter.scooter_id}`;
  const photoUrl = scooter.photos?.[0] ? getFileUrl(scooter.photos[0]) : null;
  const mechanicalCheck = scooter.mechanical_check || {};
  const serviceInfo = mechanicalCheck.service_info || {};
  
  // Calculate average score
  const allKeys = CHECKLIST_CATEGORIES.flatMap(c => c.items.map(i => i.key));
  const scores = allKeys.map(k => mechanicalCheck[k] ?? 3);
  const avgScore = (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Fișă Scuter - ${scooter.scooter_id}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 11px;
      line-height: 1.4;
      color: #1a1a1a;
      background: white;
    }
    .page {
      width: 210mm;
      min-height: 297mm;
      padding: 10mm;
      margin: 0 auto;
    }
    @media print {
      .page {
        padding: 8mm;
        margin: 0;
      }
      @page {
        size: A4;
        margin: 0;
      }
    }
    
    /* Header */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 3px solid #BEF264;
      padding-bottom: 10px;
      margin-bottom: 15px;
    }
    .logo {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .logo-box {
      width: 45px;
      height: 45px;
      background: #BEF264;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 900;
      font-size: 18px;
      color: black;
    }
    .logo-text h1 {
      font-size: 18px;
      font-weight: 900;
      letter-spacing: 1px;
    }
    .logo-text p {
      font-size: 10px;
      color: #666;
    }
    .qr-section {
      text-align: center;
    }
    .qr-section svg {
      width: 70px;
      height: 70px;
    }
    .qr-section p {
      font-size: 8px;
      color: #666;
      margin-top: 3px;
    }
    
    /* Main Info */
    .main-info {
      display: flex;
      gap: 15px;
      margin-bottom: 15px;
    }
    .photo-container {
      width: 180px;
      height: 135px;
      border-radius: 8px;
      overflow: hidden;
      background: #f0f0f0;
      flex-shrink: 0;
    }
    .photo-container img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .scooter-details {
      flex: 1;
    }
    .scooter-title {
      font-size: 22px;
      font-weight: 900;
      margin-bottom: 5px;
    }
    .scooter-id {
      display: inline-block;
      background: #BEF264;
      color: black;
      padding: 2px 8px;
      border-radius: 4px;
      font-weight: bold;
      font-size: 12px;
      margin-bottom: 10px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }
    .info-item {
      background: #f5f5f5;
      padding: 6px 8px;
      border-radius: 4px;
    }
    .info-item label {
      display: block;
      font-size: 9px;
      color: #666;
      text-transform: uppercase;
    }
    .info-item span {
      font-weight: 600;
      font-size: 12px;
    }
    
    /* Service Info */
    .service-info {
      background: #f0f9ff;
      border: 1px solid #bae6fd;
      border-radius: 8px;
      padding: 10px;
      margin-bottom: 15px;
    }
    .service-info h3 {
      font-size: 11px;
      color: #0284c7;
      margin-bottom: 8px;
      text-transform: uppercase;
    }
    .service-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
    }
    
    /* Checklist Section */
    .checklist-section {
      margin-bottom: 12px;
    }
    .checklist-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      color: #166534;
      border-bottom: 2px solid #BEF264;
      padding-bottom: 3px;
      margin-bottom: 6px;
    }
    .checklist-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 3px;
    }
    .checklist-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 3px 5px;
      background: #fafafa;
      border-radius: 3px;
      font-size: 9px;
    }
    .checklist-item span:first-child {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-right: 5px;
    }
    .rating-badge {
      width: 18px;
      height: 18px;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 10px;
      flex-shrink: 0;
    }
    
    /* Conclusion */
    .conclusion {
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
      border: 2px solid #BEF264;
      border-radius: 8px;
      padding: 12px;
      margin-bottom: 15px;
    }
    .conclusion-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
    }
    .conclusion h3 {
      font-size: 12px;
      text-transform: uppercase;
      color: #166534;
    }
    .score-badge {
      background: #166534;
      color: white;
      padding: 5px 12px;
      border-radius: 20px;
      font-weight: bold;
      font-size: 14px;
    }
    .approval-status {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: bold;
      font-size: 13px;
    }
    .approval-status.approved {
      color: #166534;
    }
    .approval-status.not-approved {
      color: #dc2626;
    }
    .recommendations {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #bbf7d0;
    }
    .recommendations label {
      font-size: 9px;
      color: #666;
      text-transform: uppercase;
    }
    .recommendations p {
      margin-top: 3px;
    }
    
    /* Documents */
    .documents {
      background: #fefce8;
      border: 1px solid #fde047;
      border-radius: 8px;
      padding: 10px;
      margin-bottom: 15px;
    }
    .documents h3 {
      font-size: 11px;
      color: #a16207;
      margin-bottom: 8px;
      text-transform: uppercase;
    }
    .doc-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }
    
    /* Technical File */
    .technical {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 10px;
      margin-bottom: 15px;
    }
    .technical h3 {
      font-size: 11px;
      color: #525252;
      margin-bottom: 8px;
      text-transform: uppercase;
    }
    
    /* Rating Legend */
    .legend {
      display: flex;
      justify-content: center;
      gap: 10px;
      margin-bottom: 12px;
      padding: 8px;
      background: #f5f5f5;
      border-radius: 6px;
    }
    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 8px;
    }
    .legend-badge {
      width: 14px;
      height: 14px;
      border-radius: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 8px;
    }
    
    /* Footer */
    .footer {
      margin-top: 15px;
      padding-top: 10px;
      border-top: 1px solid #e5e5e5;
      text-align: center;
      font-size: 9px;
      color: #666;
    }
    .footer .legal {
      font-style: italic;
      margin-top: 5px;
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Header -->
    <div class="header">
      <div class="logo">
        <div class="logo-box">JP</div>
        <div class="logo-text">
          <h1>SCUTERSH</h1>
          <p>Premium Scooters • Fișă Tehnică</p>
        </div>
      </div>
      <div class="qr-section">
        <div style="width: 80px; height: 80px;">
          ${qrSvg || '<div style="width:80px;height:80px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;font-size:8px;">QR Code</div>'}
        </div>
        <p>Scanează pentru detalii</p>
      </div>
    </div>
    
    <!-- Main Info -->
    <div class="main-info">
      ${photoUrl ? `
      <div class="photo-container">
        <img src="${photoUrl}" alt="${scooter.brand} ${scooter.model}" />
      </div>
      ` : ''}
      <div class="scooter-details">
        <h2 class="scooter-title">${scooter.brand} ${scooter.model}</h2>
        <span class="scooter-id">${scooter.scooter_id}</span>
        <div class="info-grid">
          <div class="info-item">
            <label>An fabricație</label>
            <span>${scooter.year}</span>
          </div>
          <div class="info-item">
            <label>Cilindree</label>
            <span>${scooter.engine_cc} cc</span>
          </div>
          <div class="info-item">
            <label>Culoare</label>
            <span>${scooter.color}</span>
          </div>
          <div class="info-item">
            <label>Kilometraj</label>
            <span>${scooter.mileage.toLocaleString()} km</span>
          </div>
          <div class="info-item">
            <label>VIN / Șasiu</label>
            <span style="font-size: 10px;">${scooter.vin}</span>
          </div>
          <div class="info-item">
            <label>Status</label>
            <span>${scooter.status === 'in_stock' ? 'În stoc' : scooter.status === 'reserved' ? 'Rezervat' : scooter.status}</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Service Info -->
    ${serviceInfo.dernier_vidange_effectue || serviceInfo.date_derniere_vidange ? `
    <div class="service-info">
      <h3>📅 Informații Service</h3>
      <div class="service-grid">
        <div class="info-item">
          <label>Schimb ulei</label>
          <span>${serviceInfo.dernier_vidange_effectue ? '✓ Efectuat' : 'Nu'}</span>
        </div>
        <div class="info-item">
          <label>Efectuat de</label>
          <span>${serviceInfo.vidange_effectue_par === 'jedda' ? 'Scutersh' : serviceInfo.vidange_effectue_par === 'autre' ? 'Alt service' : 'Necunoscut'}</span>
        </div>
        <div class="info-item">
          <label>Data</label>
          <span>${serviceInfo.date_derniere_vidange || '-'}</span>
        </div>
        <div class="info-item">
          <label>La km</label>
          <span>${serviceInfo.km_derniere_vidange ? serviceInfo.km_derniere_vidange.toLocaleString() : '-'}</span>
        </div>
      </div>
    </div>
    ` : ''}
    
    <!-- Rating Legend -->
    <div class="legend">
      ${Object.entries(RATING_LABELS).map(([val, info]) => `
        <div class="legend-item">
          <div class="legend-badge" style="background: ${info.bg}">${val}</div>
          <span>${info.description}</span>
        </div>
      `).join('')}
    </div>
    
    <!-- Mechanical Checklist -->
    ${CHECKLIST_CATEGORIES.map(category => `
    <div class="checklist-section">
      <h4 class="checklist-title">${category.title}</h4>
      <div class="checklist-grid">
        ${category.items.map(item => {
          const value = mechanicalCheck[item.key] ?? 3;
          const ratingInfo = RATING_LABELS[value] || RATING_LABELS[3];
          return `
          <div class="checklist-item">
            <span>${item.label}</span>
            <div class="rating-badge" style="background: ${ratingInfo.bg}">${value}</div>
          </div>
          `;
        }).join('')}
      </div>
    </div>
    `).join('')}
    
    <!-- Conclusion -->
    <div class="conclusion">
      <div class="conclusion-header">
        <h3>📊 Concluzie Tehnică</h3>
        <span class="score-badge">${avgScore} / 5</span>
      </div>
      <div class="approval-status ${mechanicalCheck.aprobat_utilizare !== false ? 'approved' : 'not-approved'}">
        ${mechanicalCheck.aprobat_utilizare !== false ? '✓ APROBAT PENTRU UTILIZARE' : '✗ NEAPROBAT PENTRU UTILIZARE'}
      </div>
      ${mechanicalCheck.recomandari_mecanic ? `
      <div class="recommendations">
        <label>Recomandări mecanic:</label>
        <p>${mechanicalCheck.recomandari_mecanic}</p>
      </div>
      ` : ''}
    </div>
    
    <!-- Documents -->
    <div class="documents">
      <h3>📄 Situație Acte</h3>
      <div class="doc-grid">
        <div class="info-item">
          <label>Acte disponibile</label>
          <span>${mechanicalCheck.acte_disponibile !== false ? '✓ Da' : '✗ Nu'}</span>
        </div>
        <div class="info-item">
          <label>Tip documente</label>
          <span>${mechanicalCheck.tip_acte === 'carte_rar' ? 'Carte RAR România' : mechanicalCheck.tip_acte === 'carte_strainatate' ? 'Carte străinătate' : 'Fără acte'}</span>
        </div>
        ${mechanicalCheck.tara_documente ? `
        <div class="info-item">
          <label>Țara</label>
          <span>${mechanicalCheck.tara_documente}</span>
        </div>
        ` : ''}
      </div>
      ${mechanicalCheck.observatii_acte ? `<p style="margin-top: 8px; font-size: 10px;">${mechanicalCheck.observatii_acte}</p>` : ''}
    </div>
    
    <!-- Technical File -->
    ${scooter.technical_file && Object.values(scooter.technical_file).some(v => v) ? `
    <div class="technical">
      <h3>⚙️ Fișă Tehnică</h3>
      <div class="info-grid">
        ${scooter.technical_file.engine_type ? `<div class="info-item"><label>Tip motor</label><span>${scooter.technical_file.engine_type}</span></div>` : ''}
        ${scooter.technical_file.transmission ? `<div class="info-item"><label>Transmisie</label><span>${scooter.technical_file.transmission}</span></div>` : ''}
        ${scooter.technical_file.cooling_system ? `<div class="info-item"><label>Răcire</label><span>${scooter.technical_file.cooling_system}</span></div>` : ''}
        ${scooter.technical_file.fuel_type ? `<div class="info-item"><label>Combustibil</label><span>${scooter.technical_file.fuel_type}</span></div>` : ''}
        ${scooter.technical_file.max_speed ? `<div class="info-item"><label>Viteză max</label><span>${scooter.technical_file.max_speed}</span></div>` : ''}
        ${scooter.technical_file.consumption ? `<div class="info-item"><label>Consum</label><span>${scooter.technical_file.consumption}</span></div>` : ''}
      </div>
    </div>
    ` : ''}
    
    <!-- Footer -->
    <div class="footer">
      <p><strong>SCUTERSH</strong> • Scutere & Piese Premium</p>
      <p class="legal">Această fișă reflectă starea scuterului la momentul verificării. Uzura ulterioară nu poate fi imputată Scutersh.</p>
      <p style="margin-top: 5px;">Data generării: ${new Date().toLocaleDateString('ro-RO')}</p>
    </div>
  </div>
</body>
</html>
  `;
}

export default PrintScooterSheet;
